import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {UntypedFormBuilder} from '@angular/forms';
import {CompanyService} from '@docma-mm-core/company/company.service';
import {Contact} from '@docma-mm-core/contact/contact';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {FieldPermission} from '@docma-mm-core/field/field-permission.enum';
import {Structure} from '@docma-mm-core/structure/structure';
import {filter, first} from 'rxjs/operators';
import {MmProjectLoaderService} from '../../../mm-project/mm-project-loader.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {BehaviorSubject, combineLatest, firstValueFrom} from 'rxjs';
import {FaultViewEnum, MmFaultViewService} from '../mm-fault-view.service';
import {MmFaultFormService} from '../mm-fault-form.service';
import {FaultFieldGroups} from './fault-field-groups';
import {TranslateService} from '@ngx-translate/core';
import {ViewPoint} from '@docma-mm-core/status/viewpoint.enum';
import {MmProjectPermissionsService} from '../../../mm-project/mm-project-permissions-service';
import {MmPlanEditService} from '../../mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {MmPlansManagerService} from '../../mm-plans-view/services/mm-plans-manager.service';
import {MmFaultRightPaneService} from '../../mm-faults-view/mm-fault-right-pane/mm-fault-right-pane.service';
import {MmFaultRightPanePageEnum} from '../../mm-faults-view/mm-fault-right-pane/mm-fault-right-pane.enum';
import {MmFaultsViewService} from '../../mm-faults-view/services/mm-faults-view.service';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {FaultSyncService} from '@docma-mm-core/fault/sync/fault-sync.service';
import {FaultPersistenceService} from '@docma-mm-core/fault/fault-persistence.service';
import {MmFaultDetailsService} from './services/mm-fault-detail.service';
import {DynamicFormReadyEvent} from '@mydocma/dynamic-form';
import {ConnectivityService} from '@mydocma/core';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {FieldConfigurationService} from '../mm-field-configuration/services/field-configuration.service';
import {MmFaultChangePageService} from '../../mm-faults-view/mm-fault-right-pane/mm-fault-change-page.service';
import {MmFaultsLoaderService} from '../../mm-faults-loader.service';
import {FaultChangesCommitService} from '@docma-mm-core/fault/fault-changes-commit.service';
import {FaultPatchService} from '@docma-mm-core/fault/patch/fault-patch-service';
import {ProjectService} from '@mydocma/project';

export interface SourceItem {
  id: number;
  text: string;
}

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-fault-detail-form',
  templateUrl: './mm-fault-detail-form.component.html',
  styleUrls: ['./mm-fault-detail-form.component.scss'],
  providers: []
})
export class MmFaultDetailFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() faultItem: ViewFault = null;
  @Input() isUpdate: boolean;

  @Input() isNewSubject = new BehaviorSubject<boolean>(false);
  isNew$ = this.isNewSubject.asObservable();

  @Input() isLocked: boolean;

  @Input()
  set active(isActive: boolean) {
    if (isActive) {
      this.cdr.reattach();
      this.cdr.markForCheck();
    } else {
      this.cdr.detach();
    }
  }

  isFaultInfoOpen = false;

  @ViewChild('tooltip', {static: false})
  tooltipButton: ElementRef;

  structure: Structure;

  @Output() formCloseEvent: EventEmitter<ViewFault> = new EventEmitter<ViewFault>();
  @Output() isCreatingEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() faultFormValidEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onFormReady: EventEmitter<DynamicFormReadyEvent> = new EventEmitter<DynamicFormReadyEvent>();

  hasChangesSubject = new BehaviorSubject<boolean>(false);
  hasChanges$ = this.hasChangesSubject.asObservable();



  @ViewChild('textField', { static: true })
  textFieldTemplateRef: TemplateRef<any>;
  @ViewChild('textareaField', { static: true })
  textareaFieldTemplateRef: TemplateRef<any>;
  @ViewChild('numberField', { static: true })
  numberFieldTemplateRef: TemplateRef<any>;
  @ViewChild('dateField', { static: true })
  dateFieldTemplateRef: TemplateRef<any>;
  @ViewChild('labelField', { static: true })
  labelFieldTemplateRef: TemplateRef<any>;
  @ViewChild('structureField', { static: true })
  structureFieldTemplateRef: TemplateRef<any>;
  @ViewChild('selectField', { static: true })
  selectFieldTemplateRef: TemplateRef<any>;
  @ViewChild('switchField', { static: true })
  switchFieldTemplateRef: TemplateRef<any>;

  loading: boolean;

  faultRightPane = MmFaultRightPanePageEnum.fault;

  fieldGroups: FaultFieldGroups;

  formErrors: { [key: string]: string } = {};

  fieldRequired = FieldPermission.Mandatory;
  fieldRequiredMessage = this.translateService.instant('MM.general.misc.inputIsRequired');

  loadStatus = LoadStatusEnum;

  private faultChanged = new BehaviorSubject<ViewFault>(null);
  private reloadSubject = new BehaviorSubject<{forceReload: boolean, discard: boolean, fault: ViewFault}>({forceReload: false, discard: false, fault: undefined});

  private initializedSubject = new BehaviorSubject<boolean>(false);

  constructor(private formService: MmFaultFormService,
              private readonly translateService: TranslateService,
              private readonly elementRef: ElementRef,
              private readonly formBuilder: UntypedFormBuilder,
              private readonly companyService: CompanyService,
              private readonly viewService: MmFaultViewService,
              private readonly faultsViewService: MmFaultsViewService,
              public readonly projectLoaderService: MmProjectLoaderService,
              public readonly permissionService: MmProjectPermissionsService,
              public readonly planEditService: MmPlanEditService,
              public readonly plansManagerService: MmPlansManagerService,
              private readonly faultRightPaneService: MmFaultRightPaneService,
              private readonly faultSyncService: FaultSyncService,
              private readonly persistenceService: FaultPersistenceService,
              private readonly commitService: FaultChangesCommitService,
              protected readonly patchService: FaultPatchService,
              public readonly faultDetailsService: MmFaultDetailsService,
              public readonly mmFaultRightPaneService: MmFaultRightPaneService,
              private readonly connectivityService: ConnectivityService,
              private readonly dialogService: PromptDialogService,
              private readonly fieldConfigurationService: FieldConfigurationService,
              readonly changeService: MmFaultChangePageService,
              private readonly faultsLoaderService: MmFaultsLoaderService,
              private readonly projectService: ProjectService,
              private readonly cdr: ChangeDetectorRef) {
    this.faultsLoaderService.onFaultRefreshed
        .pipe(untilDestroyed(this))
        .subscribe(f => {
          this.reloadSubject.next({forceReload: true, discard: false, fault: f});
        });

    translateService.onLangChange
    .pipe(untilDestroyed(this))
    .subscribe(l => {
      this.faultDetailsService.validateFields(null);
      this.faultDetailsService.validate();
      this.updateErrorMessages();
    });

    this.faultSyncService.syncFaultsState$
      .pipe(untilDestroyed(this))
      .subscribe(params => {
        const syncStatusChange = params.changes.find(x => x.faultUid === this.faultItem?.uid);

        if (syncStatusChange) {
          this.faultDetailsService.validateFields(syncStatusChange);
        }
      });

    // const fields = this.projectLoaderService.fields.pipe(first()).toPromise();
    // this.faultDetailFormService.initialize(fields);

    // let fields: MmModel<FieldMinimal[]>;

    // this.projectLoaderService.fields.subscribe(fields => {
    //   this.faultDetailFormService.initialize(fields);
    // })

    this.faultDetailsService.valueChanged
      .pipe(untilDestroyed(this))
      .subscribe(async () => {
        const hasChanges = await this.faultDetailsService.hasChanges();
        this.hasChangesSubject.next(hasChanges);
      });


    // this.projectLoaderService.fields
    //   .pipe(untilDestroyed(this))
    //   .subscribe(async fields => {
    //     console.warn('MmFaultFormService.initialize.0')
    //     await this.faultDetailsService.initialize(fields);
    //   });

    // this.projectLoaderService.fields.pipe(tap(async f => await this.faultDetailsService.initialize(f))),
    // this.filterService.filteredFields$

    combineLatest([this.projectLoaderService.fields, this.fieldConfigurationService.fields$.pipe(filter(Boolean)), this.projectLoaderService.userDefineds])
      .pipe(untilDestroyed(this))
      .subscribe(async ([f, fc]) => {
        // console.warn('MmFaultFormService.initialize.1');
        await this.faultDetailsService.initialize(f, fc);
        this.initializedSubject.next(true);
      });

    combineLatest([this.initializedSubject.pipe(filter(Boolean)),
      this.faultChanged,
      this.reloadSubject,
      this.faultDetailsService.onFormInitialized]
    ).pipe(untilDestroyed(this))
     .subscribe(async ([isInitialized, fault, reloadParam]) => {    // permittedFields
       // console.warn('MmFaultFormService.combineLatest.2')
       // console.warn(fault);
       // console.warn(isInitialized);

        if (fault && isInitialized && fault.data.UniqueId) {
          await this.faultDetailsService.loadFault(fault, (reloadParam.forceReload || reloadParam.discard));
        }
    });
  }

  ngOnDestroy(): void {
  }

  ngOnInit() {
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.faultItem && this.faultRightPaneService.pagesPath.findIndex(x => x === MmFaultRightPanePageEnum.fault) !== -1) {
      if (changes.faultItem?.currentValue != null) {
        this.faultDetailsService.showLoader(changes.faultItem?.currentValue);

        const fault = await changes.faultItem.currentValue.loadState$
          .pipe(filter((s: LoadState<any>) => s && (s.status === LoadStatusEnum.Loaded || s.status === LoadStatusEnum.Error)),
            first()).toPromise();

        this.isNewSubject.next((changes.faultItem.currentValue?.isNew ?? false));
        /* update current sync status */
        if (changes.faultItem.currentValue) {
          // const project = await firstValueFrom(this.projectService.currentProject);
          // const allPatches = await this.patchService.getAll(project.id);
          // await this.patchService.loadSyncStates(allPatches, [changes.faultItem.currentValue]);
          const isOffline = await firstValueFrom(this.connectivityService.appIsInOfflineMode);
          if (isOffline) {
            await this.persistenceService.updateSyncStatus(changes.faultItem.currentValue);
          }
        }
        this.reloadSubject.value.forceReload = false;
        this.reloadSubject.value.discard = false;
        this.faultChanged.next(changes.faultItem.currentValue);
      } else {
        this.isNewSubject.next(false);
      }
    }
    else if (changes.faultItem && changes.faultItem.currentValue == null) {
      this.faultChanged.next(null);
      await this.faultDetailsService.clearFault();
    }
  }

  formReady(event: DynamicFormReadyEvent) {
    this.faultDetailsService.onFormReady(event);
    this.onFormReady.emit(event);
  }

  public async discardChanges(forceReload: boolean = false) {
    this.faultFormValidEvent.emit(true);

    const isNewFault = (this.faultDetailsService.fault?.id === undefined);
    if (!isNewFault) {
        this.reloadSubject.next({forceReload, discard: true, fault: undefined});
    }
    await this.faultDetailsService.discardChanges();
  }

  focusField(fieldName: string) {
    const nativeElementes = this.elementRef.nativeElement.querySelectorAll(`[id = "${fieldName}" ]`);
    if (nativeElementes.length > 0) {
      nativeElementes[0].focus();
    }
  }

  validate(): boolean {
    const isValid = this.faultDetailsService.validate();
    this.faultFormValidEvent.emit(isValid);
    return isValid;
  }

  async getChanges(applyToData?: FaultData): Promise<FaultData> {
    return this.faultDetailsService.getChanges();
  }

  updateErrorMessages() {
    this.formErrors = {};
    this.fieldRequiredMessage = this.translateService.instant('MM.general.misc.inputIsRequired');
    const errMessage = this.fieldRequiredMessage;
    // this.fieldGroups.descriptionFields.forEach(field => {
    //   const control = this.faultForm.get(field.Prop);
    //   if (control &&
    //     control.invalid &&
    //     control.errors.required &&
    //     !this.formErrors[field.Prop]) {
    //     this.formErrors[field.Prop] = `${errMessage}`;
    //   }
    // });
    // this.fieldGroups.executionFields.forEach(field => {
    //   const control = this.faultForm.get(field.Prop);
    //   if (control &&
    //     control.invalid &&
    //     control.errors.required &&
    //     !this.formErrors[field.Prop]) {
    //     this.formErrors[field.Prop] = `${errMessage}`;
    //   }
    // });
    // this.fieldGroups.responsibleFields.forEach(field => {
    //   const control = this.faultForm.get(field.Prop);
    //   if (control &&
    //     control.invalid &&
    //     control.errors.required &&
    //     !this.formErrors[field.Prop]) {
    //     this.formErrors[field.Prop] = `${errMessage}`;
    //   }
    // });
    // this.fieldGroups.classificationFields.forEach(field => {
    //   const control = this.faultForm.get(field.Prop);
    //   if (control &&
    //     control.invalid &&
    //     control.errors.required &&
    //     !this.formErrors[field.Prop]) {
    //     this.formErrors[field.Prop] = `${errMessage}`;
    //   }
    // });
    // this.fieldGroups.projectFields.forEach(field => {
    //   const control = this.faultForm.get(field.Prop);
    //   if (control &&
    //     control.invalid &&
    //     control.errors.required &&
    //     !this.formErrors[field.Prop]) {
    //     this.formErrors[field.Prop] = `${errMessage}`;
    //   }
    // });
    // this.fieldGroups.costFields.forEach(field => {
    //   const control = this.faultForm.get(field.Prop);
    //   if (control &&
    //     control.invalid &&
    //     control.errors.required &&
    //     !this.formErrors[field.Prop]) {
    //     this.formErrors[field.Prop] = `${errMessage}`;
    //   }
    // });
  }

  searchContacts(term: string, item: Contact) {
    const safe = this.escapeRegExp(term);
    const pattern = new RegExp(safe, 'gi');
    return item.FirstName && item.FirstName.match(pattern) || item.LastName && item.LastName.match(pattern);
  }

  searchByPropertyName(term: string, item: any) {
    const safe = this.escapeRegExp(term);
    const pattern = new RegExp(safe, 'gi');
    return item.Name1 && item.Name1.match(pattern) || item.Name2 && item.Name2.match(pattern);
  }

  searchByPropertyDescription(term: string, item: any) {
    const safe = this.escapeRegExp(term);
    const pattern = new RegExp(safe, 'gi');
    return item.Description1 && item.Description1.match(pattern) || item.Description2 && item.Description2.match(pattern);
  }

  // Escape user-provided strings before using in RegExp to avoid invalid regex errors
  private escapeRegExp(input: string): string {
    if (!input) { return '';
    }
    // Escape characters with special meaning in RegExp
    return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }


  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isFaultInfoOpen &&  this.tooltipButton && !this.tooltipButton.nativeElement.contains(event.target) ) {
            this.isFaultInfoOpen = false;
    }
  }

  async setDefaultValueVisible(faultData: FaultData) {
    if (!faultData.Id) {

      const projectUserPerms = await this.projectLoaderService.projectUserPermissions.pipe(filter(m => m.loadState.status === LoadStatusEnum.Loaded),
                                                                                     first()).toPromise();
      const projectSettings  = await this.projectLoaderService.projectSettings.pipe(filter(m => m.loadState.status === LoadStatusEnum.Loaded),
                                                                             first()).toPromise();
      let IsVisibleForCO;
      let IsVisibleForCT;

      if (projectUserPerms.data.User.Company.ViewPoint === ViewPoint.Contractor) {
        IsVisibleForCO = projectSettings.data.find(s => s.Key === 'DefaultVisibilityANAN').Setting;
        IsVisibleForCT = projectSettings.data.find(s => s.Key === 'DefaultVisibilityANAG').Setting;
      } else if (projectUserPerms.data.User.Company.ViewPoint === ViewPoint.Customer) {
        IsVisibleForCO = projectSettings.data.find(s => s.Key === 'DefaultVisibilityAGAN').Setting;
        IsVisibleForCT = projectSettings.data.find(s => s.Key === 'DefaultVisibilityAGAG').Setting;
      }

      if (this.fieldGroups.responsibleFields.find(p => p.Id === 'IsVisibleForCO')) {
        faultData.IsVisibleForCO = IsVisibleForCO === 'True' ? true : false;
      }
      if (this.fieldGroups.responsibleFields.find(p => p.Id === 'IsVisibleForCT')) {
        faultData.IsVisibleForCT = IsVisibleForCT === 'True' ? true : false;
      }
    }
  }

  async onOpenFieldConfiguration() {
    if (!this.connectivityService.isOfflineVal) {
      this.viewService.requestToggleView(FaultViewEnum.ConfigFields);
    } else {
      this.dialogService.showNotificationDialog({
        title: this.translateService.instant('MM.field-configuration.offline.title'),
        subTitle: this.translateService.instant('MM.field-configuration.offline.subtitle'),
      });
    }
  }
}
